import path from 'path';
import { getPath, pageNames, getSingleMakeModelTrim, getLocationFromUrl, formatTrimCode } from '@atc/bonnet-paths';
import _isEmpty from 'lodash/isEmpty';
import { getReference } from '@atc/bonnet-reference';
const verifyAndSubstituteTrimCode = async function () {
  let query = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const trimSubstitution = {};

  // regex for strings containing '+', '-', '_' ,' ' and !
  const trimsWithSpecialCharRgx = new RegExp(/([0-9a-zA-Z]*[+\-_\s!]+[0-9a-zA-Z]*)+$/);
  const {
    make,
    model,
    trim
  } = getSingleMakeModelTrim(query);

  // trimCode(List) should be in the correct format '[MAKE]|[Trim Name]`
  const paramTrimCode = formatTrimCode(trim === null || trim === void 0 ? void 0 : trim.split('|')[1]);

  // if the query trimCode contains a special character, check reference data to verify its existence
  if (make && model && paramTrimCode && trimsWithSpecialCharRgx.test(trim)) {
    const {
      success,
      payload
    } = await getReference('trimCode', {
      makeCode: make.toUpperCase(),
      modelCode: model.toUpperCase()
    });
    if (success) {
      const trimPayload = payload.find(_ref => {
        let {
          code
        } = _ref;
        return formatTrimCode(code) === paramTrimCode;
      });
      const trimCode = trimPayload === null || trimPayload === void 0 ? void 0 : trimPayload.code;
      if (trimCode) {
        trimSubstitution['trim-sub'] = formatTrimCode(trimCode);
      }
    }
  }
  return trimSubstitution;
};

/**
 * 301 redirect legacy seo pattern urls to new patterns
 */
export default function seoRedirect() {
  let {
    allowedPatterns = {}
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return async (ctx, next) => {
    var _ctx$data;
    const brand = ctx === null || ctx === void 0 || (_ctx$data = ctx.data) === null || _ctx$data === void 0 ? void 0 : _ctx$data.brand;
    const pageName = ctx === null || ctx === void 0 ? void 0 : ctx.pageName;

    // this script will only redirect requests to the server and requests that have an actual pathname
    if (ctx.req && ctx.req.originalUrl !== '/') {
      const url = new URL(ctx.req.originalUrl, `${ctx.req.protocol}://${ctx.req.headers.host}`);
      const originalPathname = url.pathname;
      const originalSearch = url.search;
      const trimSubstitution = await verifyAndSubstituteTrimCode(ctx === null || ctx === void 0 ? void 0 : ctx.query);
      allowedPatterns = {
        ...allowedPatterns,
        ...trimSubstitution
      };

      // If you want to skip pattern matching below for a specific pattern, pass in a flat object to
      // replace the pattern with a substitution string. For example, if don't want the '+' in
      // kia/soul/+ to trigger a redirect, you can pass an object param, such as { sub-0: 'soul/+' },
      // and the new string will be kia/sub-0. If other patters trigger a redirect, soul/+ will be
      // swapped back in the place of 'sub-0' at the end
      // allowedPatterns are also generated if the current trimCode contains any of the special characters ('+', '-', '_' ,' ' and !)
      Object.keys(allowedPatterns).forEach(substitutionString => {
        url.pathname = url.pathname.replace(allowedPatterns[substitutionString], substitutionString);
      });
      const regexpTests = [
      // If a URL has a trailing slash, it should redirect to the equivalent with no trailing slash
      RegExp(/^(.)*\/$/),
      // If a URL has uppercase letters, it should redirect to the equivalent with all lowercase letters
      // If a URL has a plus(+), space() it should redirect to the equivalent with a dash(-) replacing the plus(+), space(), or slash(/)
      // If a URL has an apostrophe(') or period (.), it should redirect to the equivalent with the apostrophe (') or period(.) removed
      RegExp(/[A-Z\s+'.]+/g),
      // If a URL has the pattern "-&-" or "+&+", it should redirect to a URL that replaces "-&-" with "-"
      RegExp(/(-&-)/), RegExp(/(\+&\+)/)];
      if (pageName === pageNames.SEARCH_RESULTS && brand) {
        switch (brand) {
          case 'atc':
          case 'carAndDriver':
            regexpTests.push(RegExp(/(all|used|used-cars|both-cars|both\+cars)/));
            regexpTests.push(RegExp(/(-~-)/));
            regexpTests.push(RegExp(/(\+~\+)/));
            regexpTests.push(RegExp(/(\.[-+])/g));
            break;
          case 'kbb':
            regexpTests.push(RegExp(/(all-cars|certified-cars|used-cars|new-cars)/));
            // Redirect KBB SRP URLs with one make. e.g.: /cars-for-sale/:make...  to /cars-for-sale/all/:make/...
            if (ctx.query.makeCodeList && [].concat(ctx.query.makeCodeList).length === 1) {
              regexpTests.push(RegExp(/cars-for-sale/));
            }
            break;
          case 'ford':
            regexpTests.push(RegExp(/(all-cars|used|used-cars|new|new-cars|certified|certified-cars)/));
            break;
          default:
            break;
        }
      }
      if (regexpTests.some(regexpTest => regexpTest.test(url.pathname))) {
        let fileExtension = path.extname(url.pathname);
        // check if '.-' or '.+' exists
        if (url.pathname.lastIndexOf('.-') !== -1 || url.pathname.lastIndexOf('.+') !== -1) {
          fileExtension = '';
        }
        // remove the file extension if one exists so we can omit it from the re-write rules
        if (fileExtension) {
          url.pathname = url.pathname.replace(fileExtension, '');
        }
        let updatedPathname = url.pathname.replace(/\/$/, '').replace(/\+&\+/g, '-').replace(/-&-/g, '-').replace(/\+~\+/g, '-').replace(/-~-/g, '-').replace(/\s\s+/g, '-').replace(/%20/g, '-').replace(/\+/g, '-').replace(/\.[-+]/g, '-').replace(/\./g, '').replace(/'/g, '').toLowerCase();
        if (pageName === pageNames.SEARCH_RESULTS && brand) {
          switch (brand) {
            case 'atc':
              // update paths ending on /all to /all-cars for ATC
              updatedPathname = updatedPathname.includes('all', updatedPathname.length - 3) ? updatedPathname.replace('all', 'all-cars') : updatedPathname;

              // used will not be replaced if last in the path
              // replace '/all/' with '/all-cars/'
              // e.g. /cars-for-sale/all/ford/thunderbird => /cars-for-sale/all-cars/ford/thunderbird
              updatedPathname = updatedPathname.replace('/all/', '/all-cars/').replace('/used/', '/').replace('/used-cars/', '/').replace('/both-cars/', '/all-cars/').replace('/both+cars/', '/all-cars/');
              break;
            case 'kbb':
              updatedPathname = updatedPathname.replace('all-cars', 'all').replace('certified-cars', 'certified').replace('new-cars', 'new').replace('used-cars', 'used');

              // Redirect KBB SRP URLs with one make. e.g.: /cars-for-sale/:make...  to /cars-for-sale/all/:make/...
              if (ctx.query.makeCodeList && [].concat(ctx.query.makeCodeList).length === 1) {
                const kbbMakeRegex = RegExp(/cars-for-sale\/(all|used|new|certified)/);
                updatedPathname = !kbbMakeRegex.test(updatedPathname) ? updatedPathname.replace('cars-for-sale', 'cars-for-sale/all') : updatedPathname;
              }
              break;
            case 'ford':
              // no seo redirects needed for ford as its now all being handled in akamai
              break;
            default:
              break;
          }
        }

        // set the pathname to be the rewritten path
        // and also add back any file extension that was originally present
        url.pathname = updatedPathname + fileExtension;

        // Swap substitution strings out with their original values
        Object.keys(allowedPatterns).forEach(substitutionString => {
          url.pathname = url.pathname.replace(substitutionString, allowedPatterns[substitutionString]);
        });
        if (originalPathname !== url.pathname) {
          const redirectUrl = [url.pathname, url.search].filter(Boolean).join('');
          ctx.statusCode = 301;
          ctx.res.redirect(301, redirectUrl);
          ctx.res.end();

          // fire next to bypass the remaining queue of context middleware
          next(`seo server redirect to ${redirectUrl}`);
        }
      }

      // SRP (all brands): redirect with getPath when query values are present and the getPath result does not match
      // should trigger after any redirects due to brand-related regex tests
      if (pageName === pageNames.SEARCH_RESULTS && !_isEmpty(ctx.query)) {
        // SRP: default listingType should be 'USED' - except for KBB
        if (!ctx.query.listingType && !ctx.query.allListingType && brand !== 'kbb') {
          ctx.query.listingType = 'USED';
        }

        // copy the ctx.query to preserve location data on national pages.
        const query = {
          ...ctx.query
        };
        const hasLocationParam = await getLocationFromUrl(url.pathname);

        // if the original url has no zip param and does not match the city-state(-zip) pattern, it should not redirect to a location url
        if (!url.searchParams.get('zip') && !hasLocationParam) {
          delete query.zip;
          delete query.state;
          delete query.city;
          delete query.location;

          // search radius should be set to 0 (National)
          // ctx.query.searchRadius = '0';
        }
        const fullPath = await getPath(pageName, query, {
          brand
        });
        const [generatedPath, generatedParams] = fullPath.split('?');
        if (generatedPath && url.pathname !== generatedPath) {
          url.pathname = generatedPath;
          url.search = generatedParams || '';
        }
        const paramsToRemove = ['trimCodeList', 'clickType', 'fuelTypeGroup'];
        // remove params that should not be in the url
        paramsToRemove.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
          }
        });
        // We need to check the original query for clickType to remove it, since it won't generate a new path like trimCodeList would.
        if (originalPathname !== url.pathname || query.clickType || originalSearch !== url.search) {
          const redirectUrl = [url.pathname, url.search].filter(Boolean).join('');
          ctx.statusCode = 301;
          ctx.res.redirect(301, redirectUrl);
          ctx.res.end();

          // fire next to bypass the remaining queue of context middleware
          next(`seo server redirect to ${redirectUrl}`);
        }
      }
    }
  };
}